import React, { useState, useRef, useEffect } from "react";
import {
  StopCircle,
  UploadCloud,
  CheckCircle,
  Play,
  Video,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import { useReactMediaRecorder } from "react-media-recorder";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useParams } from "react-router-dom";
import {
  completeAiInterview,
  endAiInterview,
  fetchAiInterviewDataDetails,
  startAiInterview,
  uploadInterviewVideo,
} from "../../store/slices/application/applicationSlice";

/**
 * Re-encodes a recorded video Blob at a smaller resolution + lower bitrate to
 * keep the upload payload small (avoids 413). Keeps the original audio track.
 * Falls back to the original blob if compression isn't supported.
 */
async function compressVideo(
  blob: Blob,
  maxWidth = 480,
  videoBitsPerSecond = 300_000,
  audioBitsPerSecond = 64_000,
): Promise<Blob> {
  try {
    const arrayBuffer = await blob.arrayBuffer();
    const newBlob = new Blob([arrayBuffer], {
      type: blob.type || "video/webm",
    });
    const url = URL.createObjectURL(newBlob);

    const video = document.createElement("video");
    video.src = url;
    video.muted = true;
    video.playsInline = true;
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () =>
        reject(new Error("Failed to load video for compression"));
    });

    // Determine scaled dimensions
    const scale = Math.min(1, maxWidth / video.videoWidth);
    const targetWidth = Math.round(video.videoWidth * scale);
    const targetHeight = Math.round(video.videoHeight * scale);

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D context unavailable");

    // canvas video stream
    const canvasStream = (canvas as any).captureStream(24) as MediaStream;
    const videoTrack = canvasStream.getVideoTracks()[0];

    // Try to grab audio from the played video element
    let audioTrack: MediaStreamTrack | null = null;
    try {
      const srcStream: MediaStream = (video as any).captureStream
        ? (video as any).captureStream()
        : (video as any).mozCaptureStream();
      audioTrack = srcStream.getAudioTracks()[0] || null;
    } catch {
      audioTrack = null;
    }

    const outStream = new MediaStream();
    outStream.addTrack(videoTrack);
    if (audioTrack) outStream.addTrack(audioTrack);

    // pick a supported mimeType
    const candidates = [
      "video/webm;codecs=vp8,opus",
      "video/webm;codecs=vp8",
      "video/webm",
    ];
    const mimeType = candidates.find(
      (m) =>
        typeof MediaRecorder !== "undefined" &&
        MediaRecorder.isTypeSupported(m),
    );

    const recorder = new MediaRecorder(
      outStream,
      mimeType
        ? { mimeType, videoBitsPerSecond, audioBitsPerSecond }
        : { videoBitsPerSecond, audioBitsPerSecond },
    );
    const chunks: Blob[] = [];
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunks.push(e.data);
    };

    const done = new Promise<Blob>((resolve) => {
      recorder.onstop = () => {
        URL.revokeObjectURL(url);
        resolve(new Blob(chunks, { type: mimeType || "video/webm" }));
      };
    });

    recorder.start();
    video.play();

    // draw frames on rAF until the video ends
    const draw = () => {
      if (video.ended || video.paused) {
        return;
      }
      ctx.drawImage(video, 0, 0, targetWidth, targetHeight);
      requestAnimationFrame(draw);
    };
    requestAnimationFrame(draw);

    await new Promise<void>((resolve) => {
      video.onended = () => resolve();
    });

    if (recorder.state !== "inactive") recorder.stop();
    return await done;
  } catch (err) {
    console.warn("Video compression failed, uploading original blob:", err);
    return blob;
  }
}

/**
 * AnswerRecorder owns the react-media-recorder hook and is mounted on
 * demand. Keeping the recorder hook OUT of the top-level component ensures
 * the questions-fetch effect always runs first (the recorder's mount effect
 * can throw `Unsupported Browser` on some mobile / non-secure origins, which
 * would otherwise abort effects queued after it).
 */
//  overflow-y-auto max-h-[calc(100vh-220px)] min-h-0
function AnswerRecorder({
  questionIndex,
  questionStatus,
  videoUrl,
  onSubmitted,
  onNext,
  onPrev,
}: {
  questionIndex: number;
  questionStatus?: string;
  videoUrl?: string | null;
  onSubmitted: (videoBlob: Blob, questionIndex: number) => Promise<void>;
  onNext: () => void;
  onPrev: () => void;
}) {
  const isSubmitted =
    questionStatus?.toLowerCase() === "submitted" ||
    questionStatus?.toLowerCase() === "processed";
  const hasPreviousAnswer = isSubmitted && !!videoUrl;
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const dispatch = useAppDispatch();
  const { appId } = useParams();

  const {
    status,
    startRecording,
    stopRecording,
    mediaBlobUrl,
    clearBlobUrl,
    previewStream,
    error,
  } = useReactMediaRecorder({
    video: true,
    audio: true,
    askPermissionOnMount: true,
    stopStreamsOnStop: false,
    mediaRecorderOptions: {
      videoBitsPerSecond: 500_000,
      audioBitsPerSecond: 64_000,
    },
    onStop: (_blobUrl, blob) => setVideoBlob(blob),
  });

  const showNextButton = isSubmitted || justSubmitted;

  const isRecording = status === "recording";
  const isCameraActive = status === "acquiring_media" || isRecording;

  // Attach live preview stream while recording; switch to recorded blob for
  // playback once recording stops. Safari/iOS need explicit .load() on swap.
  // If the question was already answered (page refresh), load the saved
  // video_url so the previous submission can be previewed.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (mediaBlobUrl) {
      el.srcObject = null;
      el.src = mediaBlobUrl;
      el.load();
      return;
    }
    if (hasPreviousAnswer && !videoBlob) {
      el.srcObject = null;
      el.src = videoUrl as string;
      el.load();
      return;
    }
    el.srcObject = previewStream || null;
  }, [previewStream, mediaBlobUrl, hasPreviousAnswer, videoUrl, videoBlob]);

  useEffect(() => {
    if (error)
      toast.error("Please allow camera and microphone permissions to proceed.");
  }, [error]);

  // Reset recorder state when moving to a new question
  useEffect(() => {
    setVideoBlob(null);
    clearBlobUrl();
    setJustSubmitted(false);
  }, [questionIndex]);

  const startInterview = async () => {
    try {
      // setLoading(true);
      // setLoadError(null);
      const res: any = await dispatch(
        startAiInterview({ appId, questinNo: questionIndex + 1 }),
      ).unwrap();
      // if (cancelled) return;
      // const questions =
      //   res?.questions ||
      //   res?.data?.questions ||
      //   res?.session?.questions ||
      //   [];
      // setINTERVIEW_QUESTIONS(questions);
    } catch (error: any) {
      console.error("Error fetching application data:", error);
      // setLoadError(error?.message || 'Failed to load interview questions.');
    } finally {
      // if (!cancelled) setLoading(false);
    }
  };

  const handleStart = () => {
    startInterview();
    setVideoBlob(null);
    clearBlobUrl();
    startRecording();
  };

  const handleRetake = () => {
    setVideoBlob(null);
    clearBlobUrl();
    startRecording();
  };

  const handleSubmit = async () => {
    if (!videoBlob) return;
    setIsUploading(true);
    try {
      const compressed = await compressVideo(videoBlob);
      await onSubmitted(compressed, questionIndex);
      // Keep the camera live: drop the recorded playback so the live preview
      // stream is shown again, and reveal the Next Question button.
      setVideoBlob(null);
      clearBlobUrl();
      setJustSubmitted(true);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      {/* Video Area */}
      {/* max-w-[260px] */}
      <div className="relative w-full aspect-[9/16] sm:aspect-video bg-gray-900 rounded-xl overflow-hidden mb-6 shadow-inner mx-auto sm:max-w-none">
        {/* {!isCameraActive && !mediaBlobUrl && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 z-10 pointer-events-none">
            <Video className="w-12 h-12 mb-3 opacity-50" />
            <p>Camera is off</p>
          </div>
        )} */}

        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isCameraActive && !mediaBlobUrl}
          controls={!!mediaBlobUrl || hasPreviousAnswer}
          className="w-full h-full object-cover"
        />

        {isRecording && (
          <div className="absolute top-4 right-4 flex items-center gap-2 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            <span className="text-white text-sm font-medium">Recording</span>
          </div>
        )}
      </div>

      {/* Controls Area */}
      <div className="flex justify-center items-center gap-4">
        {showNextButton ? (
          <div className="flex w-full sm:w-auto gap-3">
            {questionIndex > 0 && (
              <button
                onClick={onPrev}
                className="flex items-center justify-center w-full sm:w-auto gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Previous Question
              </button>
            )}
            <button
              onClick={onNext}
              className="flex items-center justify-center w-full sm:w-auto gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-lg transition-colors"
            >
              Next Question
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <>
            {!isRecording && !mediaBlobUrl && (
              <button
                onClick={handleStart}
                className="flex items-center  justify-center w-full sm:w-auto gap-2 px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg transition-colors"
              >
                <Play className="w-5 h-5" />
                Start Recording
              </button>
            )}

            {isRecording && (
              <button
                onClick={stopRecording}
                className="flex items-center justify-center w-full sm:w-auto gap-2 px-6 py-3 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-lg transition-colors"
              >
                <StopCircle className="w-5 h-5" />
                Stop Recording
              </button>
            )}

            {mediaBlobUrl && (
              <div className="flex w-full gap-4 flex-wrap">
                <button
                  onClick={handleRetake}
                  disabled={isUploading}
                  className="flex-1 px-6 py-3 w-full min-w-[200px] bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  Retake Answer
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isUploading}
                  className="flex-1 flex items-center w-full min-w-[200px]  justify-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-lg transition-colors disabled:opacity-70"
                >
                  {isUploading ? (
                    <span className="animate-pulse">Uploading...</span>
                  ) : (
                    <>
                      <UploadCloud className="w-5 h-5" />
                      Submit
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default function AiInterview() {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const { appId } = useParams();
  const [INTERVIEW_QUESTIONS, setINTERVIEW_QUESTIONS]: any = useState([]);

  // That guarantees the fetch effect below runs regardless of whether the
  // camera/MediaRecorder API is supported on the current browser/origin.
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const res: any = await dispatch(
          fetchAiInterviewDataDetails({ appId }),
        ).unwrap();
        if (cancelled) return;
        const questions =
          res?.questions ||
          res?.data?.questions ||
          res?.session?.questions ||
          [];
        setINTERVIEW_QUESTIONS(questions);
      } catch (error: any) {
        console.error("Error fetching application data:", error);
        setLoadError(error?.message || "Failed to load interview questions.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [dispatch, appId]);

  const handleSubmitted = async (videoBlob: Blob, questionIndex: any) => {
    try {
      const uploadRes: any = await dispatch(
        uploadInterviewVideo({
          payload: { file: videoBlob },
          folderName: "aiInterview",
        }),
      ).unwrap();

      const videoUrl = uploadRes?.data?.url;

      const res: any = await dispatch(
        endAiInterview({
          appId,
          questionNo: questionIndex + 1,
          video: videoUrl,
        }),
      ).unwrap();
      if (currentStep === INTERVIEW_QUESTIONS.length - 1) {
        handleCompleteSubmitted();
      }
      toast.success("Answer saved successfully!");
    } catch (error) {
      toast.error("Failed to upload video. Please try again.");
      throw error;
    }
  };

  const handleCompleteSubmitted = async () => {
    try {
      const res: any = await dispatch(
        completeAiInterview({
          appId,
        }),
      ).unwrap();
      toast.success("Answer saved successfully!");
    } catch (error) {
      toast.error("Failed to complete interview. Please try again.");
      throw error;
    }
  };

  // --- RENDERING VIEWS ---

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto mt-2 sm:mt-12 py-3 sm:p-4 bg-white md:rounded-2xl md:shadow-sm md:borde ml-3 md:ml-auto mr-3 md:mr-auto">
        <div className="flex items-center justify-between mb-4 sm:mb-8">
          <div className="h-6 w-32 bg-gray-100 rounded-full animate-pulse" />
        </div>
        <div className="h-7 w-full bg-gray-100 rounded mb-4 sm:mb-4 animate-pulse" />
        <div className="h-7 w-full bg-gray-100 rounded mb-4 sm:mb-8 animate-pulse" />
        <div className="h-4 w-2/3 bg-gray-100 rounded mb-4 sm:mb-8 animate-pulse" />
        <div className="w-full aspect-[9/16] sm:aspect-video bg-gray-100 rounded-xl mb-6 animate-pulse mx-auto sm:max-w-none max-w-[260px]" />
        <div className="flex justify-center">
          <div className="h-12 w-64 bg-gray-100 rounded-lg animate-pulse" />
        </div>
      </div>
    );
  }

  if (loadError || INTERVIEW_QUESTIONS.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-4 sm:p-12 bg-white rounded-xl shadow-sm border border-gray-100 max-w-2xl mx-auto mt-10 ml-3 md:ml-auto mr-3 md:mr-auto text-center">
        <div className="mx-auto w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
          <Video className="w-8 h-8 text-gray-400" strokeWidth={1.5} />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          No Interview Questions Found
        </h2>
        <p className="text-gray-500">
          {loadError ||
            "We couldn’t load your interview questions. Please refresh the page or try again later."}
        </p>
      </div>
    );
  }

  if (currentStep >= INTERVIEW_QUESTIONS.length) {
    return (
      <div className="flex flex-col items-center justify-center p-4 sm:p-12 bg-white rounded-xl  shadow-sm border border-gray-100 max-w-2xl mx-auto mt-10 ml-3 md:ml-auto mr-3 md:mr-auto text-center">
        <div className="mx-auto w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
          <CheckCircle className="w-8 h-8 text-green-500 strokeWidth={1.5}" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          Interview Complete
        </h2>
        <p className="text-gray-500">
          Thank you for submitting your video answers. Our team will review them
          shortly.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto sm:mt-10 p-4 sm:p-6 bg-white md:rounded-2xl md:shadow-sm md:border md:border-gray-200 mb-20">
      <div className="flex items-center justify-between mb-4 sm:mb-8">
        <span className="text-sm font-semibold text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">
          Question {currentStep + 1} of {INTERVIEW_QUESTIONS.length}
        </span>
      </div>
      <div className="flex flex-col mb-4 gap-2">
        <h2 className="text-2xl font-bold text-gray-900">
          {INTERVIEW_QUESTIONS[currentStep]?.question_detail}
        </h2>
        <p className="text-gray-500">
          {" "}
          {INTERVIEW_QUESTIONS[currentStep]?.question}
        </p>
      </div>

      <AnswerRecorder
        questionIndex={currentStep}
        questionStatus={INTERVIEW_QUESTIONS[currentStep]?.status}
        videoUrl={
          INTERVIEW_QUESTIONS[currentStep]?.video_url
            ? INTERVIEW_QUESTIONS[currentStep]?.video_url
            : ""
        }
        onSubmitted={handleSubmitted}
        onNext={() => {
          if (currentStep < INTERVIEW_QUESTIONS.length - 1) {
            setCurrentStep((prev) => prev + 1);
          } else {
            setCurrentStep(INTERVIEW_QUESTIONS.length);
          }
        }}
        onPrev={() => {
          if (currentStep > 0) {
            setCurrentStep((prev) => prev - 1);
          }
        }}
      />
    </div>
  );
}
